import { Redirect, type Href } from "@-/com.npmjs.expo-router";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { type ReactElement } from "react";

/**
 * Body of an index route whose only job is to land on a page navigator's destination — the shared
 * shape behind the Decks / Stats / Browse-tab entry routes. Redirects on the FIRST render via
 * `resolveHref()` and starts the navigator's warm-up alongside; the destination's Suspense
 * boundaries own its loading state from there. `children` is the chrome for the frame before the
 * redirect commits, so match the destination's own fallback and nothing jumps.
 *
 * The redirect never waits for data. Gating it on the warm-up only delayed the destination's mount
 * by the warm-up's full wall time (~400ms of every Browse open), and held the tab on a route that
 * could neither show the destination nor fail on its behalf.
 */
export function PageNavigatorRedirect({
  children,
  navigator,
  prefetchQueryKey,
}: {
  /** Chrome shown for the frame before the redirect commits. */
  children: ReactElement | ReactElement[];
  /** A `createPageNavigator` hook result. */
  navigator: {
    prefetch: () => Promise<Href>;
    resolveHref: () => Href;
  };
  /** Stable cache id for the background warm-up query (e.g. `"browseIndexPage"`). */
  prefetchQueryKey: string;
}) {
  useQuery({
    queryFn: () => navigator.prefetch(),
    queryKey: [prefetchQueryKey, navigator],
    // NEVER focus-gated. Han's `useQuery` defaults `subscribed` to the screen's navigation focus,
    // and an observer with `subscribed: false` never starts its fetch. This route mounts at the
    // moment a bottom tab is pressed — exactly when focus is still settling — so a focus-gated
    // warm-up would usually not start until the destination had already mounted cold.
    subscribed: true,
    throwOnError: true,
  });

  return (
    <>
      <Redirect href={navigator.resolveHref()} />
      {children}
    </>
  );
}
